import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import ActionItem from 'Components/ComponentLibrary/Components/Uncategorized/ActionItem';
import useMobileLayout from 'Hooks/useMobileLayout';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { navigateToClientPage } from 'Views/Page/Content/Templates';
import {
  FDContentContainer,
  FDStack,
} from '@facet-wealth/design';

const styles = theme => ({
  root: {
    width: '100%',
    maxWidth: 1200,
    marginBottom: theme.spacing(4),
  },
  rootMobile: {
    padding: theme.spacing(1),
  },
  actionItemsContainer: {
    backgroundColor: theme.palette.facet.backgroundGray,
    borderRadius: theme.radius.md,
    padding: theme.spacing(2),
  },
  actionItem: {
    marginBottom: theme.spacing(2),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  subtitle: {
    fontSize: theme.typography.fontSize1Rem,
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(4),
  },
  tertiaryTextContainer: {
    marginBottom: theme.spacing(4),
  },
  tertiaryText: {
    fontSize: theme.typography.fontSize1Pt25Rem,
    fontWeight: theme.typography.fontWeightExtraBold,
    textAlign: 'center',
  },
});

const Content = ({
  className,
  classes,
  collection,
}) => {
  const isMobile = useMobileLayout();
  const { loadPageContentLink } = usePageContentContext();

  const CompleteFunding = (resource) => {
    // funding instruction page takes precedence over transfer wizard
    if (resource.relationship(fr.relationships.fundingInstructionsPage.name)) {
      loadPageContentLink(resource.relationship(fr.relationships.fundingInstructionsPage.name).link('related'));
      return;
    }

    loadPageContentLink(resource.relationship(fr.relationships.transferWizard.name).link('related'));
  };

  const CompleteOpening = async (resource) => {
    const [accountOpeningStep] = await resource?.relationship(fr.relationships.accountOpeningWizard.name)?.link('related').get();
    navigateToClientPage(accountOpeningStep.link('self'));
  };

  const ACTION_MAP = {
    'open-account': (resource) => CompleteOpening(resource),
    'open-and-fund-account': (resource) => CompleteOpening(resource),
    'fund-account': (resource) => CompleteFunding(resource),
  };

  const ACTION_TITLE_MAP = {
    'open-account': 'Open Account',
    'open-and-fund-account': 'Open and Fund Account',
    'fund-account': 'Fund Account',
  };

  let subtitle = 'Thank you for completing your annual check-in.';
  if (collection.resources().list().length > 0) {
    subtitle = 'Thank you. Please take a moment to complete your outstanding account opening and/or funding tasks.';
  }

  return (
    <Box className={classNames(className, classes.root, { [classes.rootMobile]: isMobile })}>
      <Subtitle
        className={classes.subtitle}
        text={subtitle}
      />
      {collection.resources().list().length > 0
        ? (
          <FDContentContainer maxWidth={'sm'}>
            <FDStack direction={'column'} spacing={2} alignItems="flex-start" justifyContent="flex-start">
              {collection.resources().list().map(actionItem => (
                <ActionItem
                  className={classes.actionItem}
                  key={actionItem.id()}
                  name={actionItem.attr('name')}
                  status={actionItem.attr('status')}
                  actionTitle={ACTION_TITLE_MAP[actionItem.attr('actionItemType')]}
                  onClick={() => ACTION_MAP[actionItem.attr('actionItemType')](actionItem)}
                />
              ))}
            </FDStack>
          </FDContentContainer>
        )
        : null}
    </Box>
  );
};

Content.defaultProps = {
  className: '',
};

Content.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  collection: PropTypes.instanceOf(fr.Collection).isRequired,
};

Content.defaultProps = {
};

export default withStyles(styles)(Content);
